import { IconButton, Typography, Box, Divider, TextField, InputAdornment, Grid, Button, Link } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { MetrcLogo } from '../../../utility';
import EntityDetailPopup from '../../../utility/EntityDetailPopup';
import { useState, useEffect } from 'react';
import APIService from '../../../../api/apiService';
import LoadingSpinner from '../../../utility/LoadingSpinner';

const _TransferDetailsPopup = ({ open, onClose, transfer, currDarkThemeClass }) => {
  const [transferDetails, setTransferDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);

  useEffect(() => {
    const checkExtension = () => {
      // Method 1: Check for the data attribute on body
      const attrMatch = document.body.getAttribute('data-metrc-extension-installed') === 'true';

      // Method 2: Check for global window variable
      const windowMatch = window.__METRC_EXTENSION_INSTALLED__ === true;

      if (attrMatch || windowMatch) {
        setIsExtensionInstalled(true);
      }
    };

    // Listen for PONG response from extension
    const handlePong = () => {
      console.log(" PONG received from extension!");
      setIsExtensionInstalled(true);
    };
    window.addEventListener('METRC_EXTENSION_PONG', handlePong);

    // Initial check
    checkExtension();

    // Ping extension and poll
    const interval = setInterval(() => {
      checkExtension();
      if (!isExtensionInstalled) {
        console.log(" PINGing Metrc extension...");
        window.dispatchEvent(new CustomEvent('METRC_EXTENSION_PING'));
      }
    }, 2000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('METRC_EXTENSION_PONG', handlePong);
    };
  }, [isExtensionInstalled]);

  useEffect(() => {
    if (open && transfer && transfer.id) {
      const fetchDetails = async () => {
        setIsLoading(true);
        try {
          const response = await APIService.fetchInstance(`cropplan-transfers/${transfer.id}/`);
          if (response.status === 200) {
            setTransferDetails(response.data);
          }
        } catch (error) {
          console.error('Error fetching transfer details:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchDetails();
    } else {
      setTransferDetails(null);
    }
  }, [open, transfer]);

  if (!transfer) return null;

  const displayData = transferDetails || transfer;
  const isDarkTheme = Boolean(currDarkThemeClass);

  const styles = {
    content: {
      padding: '0 24px 24px 24px',
    },
    sectionTitle: {
      color: '#9ec96b',
      fontSize: '1rem',
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '16px',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        color: isDarkTheme ? '#e0e0e0' : '#333333',
        '& fieldset': {
          borderColor: isDarkTheme ? 'rgba(158, 201, 107, 0.4)' : 'rgba(158, 201, 107, 0.6)',
        },
        '&:hover fieldset': {
          borderColor: '#9ec96b',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#9ec96b',
        },
      },
      '& .MuiInputLabel-root': {
        color: isDarkTheme ? '#a0a0a0' : '#666666',
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: '#9ec96b',
      },
    },
    copyButton: {
      color: '#9ec96b',
    },
    entityBox: {
      marginBottom: '16px',
      padding: '16px',
      borderRadius: '8px',
      backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      border: isDarkTheme ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
    },
  };

  const handleCopy = text => {
    navigator.clipboard.writeText(text);
  };

  const handleCopyDataToMetrc = () => {
    console.log("Copy Data to Metrc' button clicked. Dispatching START_METRC_AUTOFILL...");
    const event = new CustomEvent('START_METRC_AUTOFILL');
    window.dispatchEvent(event);
  };

  const CopyableField = ({ label, value }) => (
    <TextField
      fullWidth
      label={label}
      value={value || ''}
      variant="outlined"
      size="small"
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => handleCopy(value)} edge="end" size="small" sx={styles.copyButton} title={`Copy ${label}`}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={styles.textField}
    />
  );

  return (
    <EntityDetailPopup
      open={open}
      onClose={onClose}
      title="Transfer Details"
      headContentText=""
      currDarkThemeClass={currDarkThemeClass}
      toShowSave={false}
      toShowValidate={false}
    >
      <Box sx={styles.content}>
        {isLoading ? (
          <LoadingSpinner currDarkThemeClass={currDarkThemeClass} message="Loading Details..." />
        ) : (
          <>
            {!isExtensionInstalled && (
              <Box sx={{ width: '100%', py: 1, px: 2, mb: 1, backgroundColor: 'rgba(255, 107, 107, 0.1)', borderRadius: '4px', textAlign: 'center' }}>
                <Typography sx={{ color: isDarkTheme ? '#ff8e8e' : '#d32f2f', fontSize: '0.9rem' }}>
                  Metrc Transfer extension not detected , please install it to automate transfer creation .
                  {/* <Link href="#" sx={{ color: '#9ec96b', fontWeight: 'bold', textDecoration: 'underline' }}>
                    Download it here
                  </Link>{' '}
                  to automate transfer creation. */}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={styles.sectionTitle}>General Details</Typography>
              {isExtensionInstalled && (
                <Button
                  variant="contained"
                  onClick={handleCopyDataToMetrc}
                  startIcon={<MetrcLogo style={{ width: 20, height: 20 }} />}
                  sx={{
                    backgroundColor: '#B1D23A',
                    color: 'white',
                    '&:hover': { backgroundColor: '#9cb933' },
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: '20px',
                    padding: '6px 16px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  Data Transfer to Metrc
                </Button>
              )}
            </Box>
            <Box sx={styles.entityBox}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CopyableField label="Destination" value={displayData.destination_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyableField label="Type" value={displayData.transfer_type_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyableField label="Route" value={displayData.planned_route} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyableField label="Invoice No." value={displayData.invoice_number} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyableField
                    label="Est. Dept"
                    value={displayData.est_dept_date_time ? new Date(displayData.est_dept_date_time).toLocaleString() : '-'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyableField
                    label="Est. Arr"
                    value={displayData.est_arr_date_time ? new Date(displayData.est_arr_date_time).toLocaleString() : '-'}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <CopyableField label="Transporter" value={displayData.transporter_name} />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2, borderColor: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

            <Typography sx={styles.sectionTitle}>Drivers</Typography>
            {displayData.drivers_details && displayData.drivers_details.length > 0 ? (
              displayData.drivers_details.map((d, i) => (
                <Box key={`driver-${i}`} sx={styles.entityBox}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="Name" value={d.driver_name} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="License" value={d.driver_license_number} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="Employee ID" value={d.employee_id} />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: isDarkTheme ? '#a0a0a0' : '#666666' }}>No drivers attached.</Typography>
            )}

            <Divider sx={{ my: 2, borderColor: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

            <Typography sx={styles.sectionTitle}>Vehicles</Typography>
            {displayData.vehicles_details && displayData.vehicles_details.length > 0 ? (
              displayData.vehicles_details.map((v, i) => (
                <Box key={`vehicle-${i}`} sx={styles.entityBox}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="Make" value={v.vehicle_make} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="Model" value={v.vehicle_model} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CopyableField label="License Plate" value={v.vehicle_license_plate_number} />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: isDarkTheme ? '#a0a0a0' : '#666666' }}>No vehicles attached.</Typography>
            )}

            <Divider sx={{ my: 2, borderColor: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

            <Typography sx={styles.sectionTitle}>Packages</Typography>
            {displayData.packages_details && displayData.packages_details.length > 0 ? (
              displayData.packages_details.map((p, i) => (
                <Box key={`package-${i}`} sx={styles.entityBox}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CopyableField label="Tag" value={p.metrc_sub_package_tag_number} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <CopyableField label="Type" value={p.sub_package_type} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <CopyableField label="Weight" value={p.current_sub_package_weight} />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: isDarkTheme ? '#a0a0a0' : '#666666' }}>No packages attached.</Typography>
            )}
          </>
        )}
      </Box>
    </EntityDetailPopup>
  );
};

export default _TransferDetailsPopup;
